import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, LogOut, User as UserIcon, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore, selectUser } from "../../store/auth.store.js";
import { useUiStore } from "../../store/ui.store.js";
import * as authApi from "../../api/auth.api.js";
import { cn } from "../../lib/cn.js";

/**
 * Topbar with sidebar toggle, breadcrumbs slot (left), and user menu (right).
 */
export function Topbar({ breadcrumbs }) {
  const user = useAuthStore(selectUser);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Server-side may already be logged out — proceed anyway.
    } finally {
      useAuthStore.getState().clear();
      toast.success("Logged out");
      navigate("/login", { replace: true });
    }
  };

  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() ?? "·";

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border-subtle bg-bg-base/90 px-6 backdrop-blur">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleSidebar}
          className="rounded-md p-2 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>
        {breadcrumbs}
      </div>

      <div className="flex items-center gap-3">
        <a
          href={import.meta.env.VITE_PUBLIC_SITE_URL ?? "http://localhost:5173"}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden items-center gap-1 rounded-md px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-elevated hover:text-text-primary md:inline-flex"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View public site
        </a>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-md py-1.5 pl-1.5 pr-2 text-sm hover:bg-bg-elevated"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-bg-base">
              {initials}
            </span>
            <span className="hidden text-left md:block">
              <p className="text-sm leading-tight text-text-primary">{user?.name}</p>
              <p className="text-[11px] leading-tight text-text-muted">
                {user?.role === "super_admin" ? "Super Admin" : "Content Editor"}
              </p>
            </span>
            <ChevronDown className="h-4 w-4 text-text-muted" />
          </button>

          <div
            className={cn(
              "absolute right-0 top-full mt-2 w-56 origin-top-right rounded-lg border border-border-subtle bg-bg-elevated shadow-card-hover transition-all",
              menuOpen ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
            )}
          >
            <div className="border-b border-border-subtle px-4 py-3">
              <p className="text-sm font-medium text-text-primary">{user?.name}</p>
              <p className="text-xs text-text-muted">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                navigate("/profile");
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-text-secondary hover:bg-bg-elevated-2 hover:text-text-primary"
            >
              <UserIcon className="h-4 w-4" /> Profile
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 border-t border-border-subtle px-4 py-2 text-left text-sm text-danger hover:bg-bg-elevated-2"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
